/**
 * Avatar e nome da pessoa no header (com pouca largura, só o avatar) e o menu
 * dela: nome e e-mail no topo, "Configurações" e "Sair". O nome segue a regra
 * de `personName` (conta pessoal, senão a primeira conta que não é a da
 * Mecanizou, senão o e-mail). Botão de menu (`aria-haspopup`) com
 * painel `role=menu`, como o menu da linha das telas de fornecedores: setas,
 * Home e End entre os itens; Esc fecha e volta ao avatar; Tab fecha; clique
 * fora fecha.
 */
import {FC, KeyboardEvent, useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';

import {useAuth} from 'app/modules/auth';
import {personInitials, personName} from 'app/shared/utils/personName';

const MENU_ID = 'opx-usermenu';

const UserMenu: FC = () => {
  const {currentUser, logout} = useAuth();
  const name = personName(currentUser);
  const email = currentUser?.user?.email || '';
  const initials = personInitials(name);

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Item que recebe o foco quando o painel abre (seta para cima: o último)
  const pendingFocus = useRef<'first' | 'last' | null>(null);

  const menuItems = () =>
    Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []
    );

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const openWith = (focus: 'first' | 'last') => {
    pendingFocus.current = focus;
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    if (pendingFocus.current) {
      const list = menuItems();
      (pendingFocus.current === 'last'
        ? list[list.length - 1]
        : list[0]
      )?.focus();
      pendingFocus.current = null;
    }
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (
        target &&
        !triggerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        close(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onTriggerKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openWith(event.key === 'ArrowUp' ? 'last' : 'first');
    }
  };

  const onPanelKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = menuItems();
    const index = list.indexOf(document.activeElement as HTMLElement);
    const move = (next: number) => {
      event.preventDefault();
      list[(next + list.length) % list.length]?.focus();
    };
    if (event.key === 'ArrowDown') move(index + 1);
    else if (event.key === 'ArrowUp') move(index < 0 ? -1 : index - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(list.length - 1);
    else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  return (
    <div className='opx-appbar__user'>
      <button
        ref={triggerRef}
        type='button'
        className='opx-appbar__avatarbtn'
        aria-haspopup='menu'
        aria-expanded={open}
        aria-controls={open ? MENU_ID : undefined}
        aria-label={name ? `Conta: ${name}` : 'Conta'}
        title={name || undefined}
        onClick={() => (open ? close(false) : openWith('first'))}
        onKeyDown={onTriggerKey}
      >
        <span className='opx-avatar opx-appbar__avatar' aria-hidden='true'>
          {initials || <i className='bi bi-person' />}
        </span>
        {name && <span className='opx-appbar__username'>{name}</span>}
      </button>
      {open && (
        <div
          ref={panelRef}
          id={MENU_ID}
          className='opx-usermenu'
          role='menu'
          aria-label='Conta'
          onKeyDown={onPanelKey}
        >
          <div className='opx-usermenu__who' role='presentation'>
            <span className='opx-usermenu__name' title={name}>
              {name}
            </span>
            {email && email !== name && (
              <span className='opx-usermenu__email' title={email}>
                {email}
              </span>
            )}
          </div>
          <Link
            to='/accounts/settings'
            role='menuitem'
            tabIndex={-1}
            className='opx-usermenu__item'
            onClick={() => close(false)}
          >
            <i className='bi bi-gear' aria-hidden='true' />
            Configurações
          </Link>
          <button
            type='button'
            role='menuitem'
            tabIndex={-1}
            className='opx-usermenu__item'
            onClick={() => {
              close(false);
              logout();
            }}
          >
            <i className='bi bi-box-arrow-right' aria-hidden='true' />
            Sair
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
